/* eslint-disable react/no-unescaped-entities */
/**
 * /hustleup — Meetup-style category browse landing.
 *
 * Hustle yellow palette to match the rest of the platform.  Cards link
 * to /hustleup/join with the category pre-selected so the interest
 * picker shows it ticked.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Users, ArrowRight, ShieldCheck, MapPin, CheckCircle2,
  Mountain, Code, Heart, Dumbbell, UtensilsCrossed, Briefcase,
  Palette, Brush, Music, Baby, Languages, Rainbow, Flower,
  Megaphone, Dices, PawPrint, Plane, GraduationCap, HandHeart,
  Waves, Car, PartyPopper, PenTool,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

const ICONS = {
  Mountain, Code, Heart, Dumbbell, UtensilsCrossed, Briefcase,
  Palette, Brush, Music, Baby, Languages, Rainbow, Flower,
  Megaphone, Dices, PawPrint, Plane, GraduationCap, HandHeart,
  Waves, Car, PartyPopper, PenTool, Users,
};

export default function HustleUpLandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [cats, setCats] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    api.get("/api/hustleup/categories").then(r => setCats(r.data || []));
    const t = localStorage.getItem("hu_session_token");
    if (t) {
      api.get(`/api/hustleup/dm/unread-count?token=${t}`)
        .then(r => setUnread(r.data?.total || 0))
        .catch(() => {});
    }
  }, [i18n.language]);

  const openSignup = (slug) => navigate(`/hustleup/join?interest=${slug}`);

  const NAV = [
    { to: "/",                     label: t("hu_nav_home",       { defaultValue: "Home" }) },
    { to: "/hustleup#categories",  label: t("hu_nav_categories", { defaultValue: "Categories" }) },
    { to: "/hustleup/groups",      label: t("hu_nav_groups",     { defaultValue: "Groups" }) },
    { to: "/hustleup/organisers",  label: t("hu_nav_organisers", { defaultValue: "For organisers" }) },
    { to: "/hustleup/me",          label: t("hu_nav_me",         { defaultValue: "My profile" }) },
    { to: "/hustleup/join",        label: t("hu_nav_join",       { defaultValue: "Join free" }), align: "right", variant: "primary" },
  ];

  return (
    <div className="hu-root" data-testid="hustleup-landing">
      <PlatformHeader
        brand={{ name: "HustleUp", to: "/" }}
        nav={NAV}
        showBookCta={false}
        showCart={false}
      />
      {unread > 0 && (
        <div
          data-testid="hu-messages-badge"
          className="max-w-6xl mx-auto px-6 pt-3"
        >
          <a
            href="/hustleup/messages"
            data-testid="hu-messages-link"
            className="inline-flex items-center gap-2 text-xs font-bold text-white bg-rose-500 rounded-full px-3 py-1"
          >
            {t("hu_messages_unread", { defaultValue: "New messages" })}
            <span className="bg-white/25 rounded-full px-1.5">
              {unread > 99 ? "99+" : unread}
            </span>
          </a>
        </div>
      )}

      <section className="hu-hero">
        <div className="hu-hero__inner">
          <span className="hu-eyebrow">Australia · membership 100% free</span>
          <h1 className="hu-h1">
            Find your <span className="hu-accent">people.</span><br/>
            Build your <span className="hu-accent">group.</span>
          </h1>
          <p className="hu-sub">
            HustleUp is Australia's free community-meetup platform — from
            barra fishing weekends in Darwin to coding workshops in
            Hobart.  Members and organisers never pay a cent.  Verified
            humans only (verified email + mobile + AU postcode).
          </p>
          <div className="hu-cta-row">
            <button onClick={() => navigate("/hustleup/join")}
                    className="hu-btn-primary" data-testid="hu-hero-join">
              Become a member <ArrowRight size={16} />
            </button>
            <a href="#categories" className="hu-btn-ghost">
              Browse categories
            </a>
            <a href="/hustleup/groups" className="hu-btn-ghost"
               data-testid="hu-hero-groups">
              Browse groups
            </a>
          </div>
          <div className="hu-trust">
            <span><ShieldCheck size={14} /> Verified members</span>
            <span><MapPin size={14} /> AU only (incl. visitors)</span>
            <span><CheckCircle2 size={14} /> Free forever</span>
          </div>
        </div>
      </section>

      <section className="hu-cats" id="categories">
        <div className="hu-cats__head">
          <h2>Browse all {cats.length} categories</h2>
          <p>Click any category to start your free membership with that
             interest pre-selected.</p>
        </div>
        <div className="hu-cats__grid" data-testid="hu-cats-grid">
          {cats.map(c => {
            const Icon = ICONS[c.icon] || Users;
            return (
              <button
                key={c.slug}
                className="hu-cat"
                onClick={() => openSignup(c.slug)}
                style={{ "--cat": c.color }}
                data-testid={`hu-cat-${c.slug}`}
              >
                <div className="hu-cat__icon"><Icon size={28} /></div>
                <div className="hu-cat__name">{c.name}</div>
                <div className="hu-cat__sub">
                  {(c.sub || []).slice(0, 3).join(" · ")}
                  {c.sub?.length > 3 && ` · +${c.sub.length - 3} more`}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="hu-pitch">
        <div className="hu-pitch__inner">
          <div>
            <h3>Free for members. Free for organisers.</h3>
            <p>
              We don't charge a cent to join, to attend, or to run your own
              group.  HustleUp is funded by the wider Hustle ecosystem —
              tackle.au, the property module, our restaurant network —
              so the social platform stays open and verified.
            </p>
          </div>
          <div>
            <h3>Verified humans only.</h3>
            <p>
              Email verification + mobile-phone verification + AU
              postcode check on signup.  No bots, no spam DMs, no
              international scam accounts pretending to be local.
            </p>
          </div>
          <div>
            <h3>Coming soon: start your group in 60 seconds.</h3>
            <p>
              Phase 2 lets any verified member spin up a group with
              name, category, description, region image and tags.
              Phase 3 wires event RSVPs, capacity caps, and waitlists.
            </p>
          </div>
        </div>
      </section>

      <SuiteFooter />

      <style>{styles}</style>
    </div>
  );
}

const styles = `
.hu-root {
  background: #fffef5; color: #0f172a;
  font-family: 'Inter', system-ui, sans-serif; min-height: 100vh;
}
.hu-topbar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 18px 32px; background: #0f172a; color: #fbbf24;
  position: sticky; top: 0; z-index: 30;
}
.hu-brand {
  display: inline-flex; align-items: center; gap: 8px;
  color: #fbbf24; text-decoration: none; font-weight: 800;
  font-family: Georgia, serif; font-size: 22px; letter-spacing: -0.02em;
}
.hu-brand span { color: #fff; }
.hu-brand span span { color: #fbbf24; }
.hu-brand small { font-size: 12px; color: #94a3b8; margin-left: 4px;
                  letter-spacing: 0.1em; font-family: Inter; }
.hu-nav { display: flex; gap: 24px; }
.hu-nav a { color: #f1f5f9; text-decoration: none; font-size: 14px; font-weight: 500; }
.hu-nav a:hover { color: #fbbf24; }
.hu-cta-pill {
  background: #fbbf24; color: #0f172a; border: none;
  padding: 10px 22px; border-radius: 999px; font-weight: 700;
  font-size: 14px; cursor: pointer;
}
.hu-cta-pill:hover { transform: translateY(-1px); }

.hu-hero {
  background:
    radial-gradient(ellipse 80% 50% at 80% 20%, rgba(251, 191, 36, 0.18), transparent 60%),
    linear-gradient(180deg, #fffef5 0%, #fef3c7 100%);
  padding: 80px 32px 100px;
}
.hu-hero__inner { max-width: 920px; margin: 0 auto; }
.hu-eyebrow {
  display: inline-block; background: rgba(15,23,42,0.08); color: #0f172a;
  padding: 5px 14px; border-radius: 999px; font-size: 11px;
  letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700;
  margin-bottom: 24px;
}
.hu-h1 {
  font-family: Georgia, serif; font-size: clamp(40px, 6vw, 80px);
  line-height: 1.0; font-weight: 800; letter-spacing: -0.035em;
  margin: 0 0 24px;
}
.hu-accent { color: #f59e0b; font-style: italic; }
.hu-sub { font-size: 18px; line-height: 1.55; color: #475569;
          max-width: 640px; margin: 0 0 36px; }
.hu-cta-row { display: flex; gap: 12px; align-items: center;
              flex-wrap: wrap; margin-bottom: 28px; }
.hu-btn-primary {
  display: inline-flex; align-items: center; gap: 8px;
  background: #0f172a; color: #fbbf24; border: none;
  padding: 16px 26px; border-radius: 12px; font-weight: 700;
  font-size: 15px; cursor: pointer;
}
.hu-btn-primary:hover { transform: translateY(-1px); }
.hu-btn-ghost {
  padding: 16px 22px; border-radius: 12px; color: #0f172a;
  font-weight: 600; text-decoration: none; font-size: 15px;
  border: 1px solid rgba(15,23,42,0.15);
}
.hu-trust {
  display: flex; gap: 22px; flex-wrap: wrap; font-size: 13px;
  color: #475569;
}
.hu-trust span { display: inline-flex; align-items: center; gap: 6px; }

.hu-cats { padding: 80px 32px; background: white; }
.hu-cats__head { max-width: 1200px; margin: 0 auto 40px; }
.hu-cats__head h2 {
  font-family: Georgia, serif; font-size: 32px; font-weight: 800;
  letter-spacing: -0.025em; margin: 0 0 8px;
}
.hu-cats__head p { color: #64748b; margin: 0; }
.hu-cats__grid {
  max-width: 1200px; margin: 0 auto;
  display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}
.hu-cat {
  background: white; border: 1px solid #e8e2d4; border-radius: 14px;
  padding: 22px; text-align: left; cursor: pointer;
  font: inherit; color: inherit; display: flex; flex-direction: column;
  gap: 8px; transition: all 0.18s ease;
  border-left: 4px solid var(--cat, #fbbf24);
}
.hu-cat:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 40px rgba(15,23,42,0.1);
  border-color: var(--cat, #fbbf24);
}
.hu-cat__icon {
  width: 48px; height: 48px; border-radius: 12px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--cat, #fbbf24); color: white; opacity: 0.92;
}
.hu-cat__name {
  font-weight: 800; font-size: 16px; letter-spacing: -0.01em;
  margin-top: 8px;
}
.hu-cat__sub {
  font-size: 12px; color: #64748b; line-height: 1.45;
}

.hu-pitch { padding: 80px 32px; background: #fef3c7; }
.hu-pitch__inner {
  max-width: 1100px; margin: 0 auto;
  display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 32px;
}
.hu-pitch h3 {
  font-family: Georgia, serif; font-size: 22px; font-weight: 800;
  letter-spacing: -0.02em; margin: 0 0 8px;
}
.hu-pitch p { color: #475569; margin: 0; line-height: 1.6; }

.hu-footer {
  display: flex; justify-content: space-between; align-items: center;
  padding: 28px 32px; background: #0f172a; color: #94a3b8;
  font-size: 13px; flex-wrap: wrap; gap: 12px;
}
.hu-footer a { color: #fbbf24; text-decoration: none; margin: 0 4px; }

@media (max-width: 640px) {
  .hu-topbar { padding: 12px 16px; gap: 8px; }
  .hu-nav { display: none; }
  .hu-hero { padding: 48px 20px 60px; }
  .hu-cats, .hu-pitch { padding: 48px 20px; }
}
`;
